import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail, MapPin, Clock3, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, PHONE, PHONE_RAW, EMAILS, ADDRESS } from "@/lib/api";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Contact({ onBook }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter your name");
    if (!/^\d{10}$/.test(form.phone)) return toast.error("Enter a valid 10-digit phone number");
    if (!form.message.trim()) return toast.error("Write a short message");
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type: "general" });
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#F8FAFC] dot-grid-dark py-20 lg:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex items-center gap-4 mb-6">
          <span className="font-mono-med text-sm text-[#D92638] border border-[#D92638]/40 rounded-full px-3.5 py-1">09</span>
          <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-500">Visit & contact</span>
          <span className="h-px flex-1 bg-slate-200" />
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] leading-tight">
              Opening soon in <span className="text-[#0D9488]">MVP Colony.</span>
            </motion.h2>
            <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
              className="mt-4 text-base text-slate-600 leading-relaxed">
              In the heart of Vizag's hospital cluster - minutes from Mahatma Gandhi Cancer Hospital, Medicover and Queen's NRI. Bookings and home collection are open now.
            </motion.p>

            <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="mt-8 space-y-3">
              <a href={`tel:${PHONE_RAW}`} data-testid="contact-phone"
                className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200/90 p-5 hover:border-[#D92638]/40 hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-[#D92638]/10 flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[#D92638]" /></div>
                <div><div className="text-xs text-slate-500">Call us</div><div className="font-semibold text-[#0B192C]">{PHONE}</div></div>
              </a>
              <a href={`https://wa.me/${PHONE_RAW}?text=${encodeURIComponent("Hi Hemozy! I have a question.")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp"
                className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200/90 p-5 hover:border-[#0D9488]/40 hover:-translate-y-0.5 transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-[#0D9488]/10 flex items-center justify-center shrink-0"><MessageCircle className="w-5 h-5 text-[#0D9488]" /></div>
                <div><div className="text-xs text-slate-500">WhatsApp</div><div className="font-semibold text-[#0B192C]">Chat with us instantly</div></div>
              </a>
              <div className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200/90 p-5" data-testid="contact-email">
                <div className="w-11 h-11 rounded-xl bg-[#0B192C]/5 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#0B192C]" /></div>
                <div>
                  <div className="text-xs text-slate-500">Email</div>
                  {EMAILS.map((em) => (
                    <a key={em} href={`mailto:${em}`} className="block font-semibold text-[#0B192C] hover:text-[#D92638] transition-colors text-sm">{em}</a>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200/90 p-5" data-testid="contact-address">
                <div className="w-11 h-11 rounded-xl bg-[#D92638]/10 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#D92638]" /></div>
                <div><div className="text-xs text-slate-500">Address</div><div className="font-semibold text-[#0B192C]">{ADDRESS}</div></div>
              </div>
              <div className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200/90 p-5" data-testid="contact-hours">
                <div className="w-11 h-11 rounded-xl bg-[#0D9488]/10 flex items-center justify-center shrink-0"><Clock3 className="w-5 h-5 text-[#0D9488]" /></div>
                <div><div className="text-xs text-slate-500">Hours</div><div className="font-semibold text-[#0B192C]">Mon-Sat 7 AM - 8 PM · Sun 8 AM - 2 PM</div></div>
              </div>
            </motion.div>
          </div>

          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="lg:col-span-7">
            <div className="relative rounded-[1.75rem] overflow-hidden border border-slate-200 h-[260px] mb-6">
              <iframe title="Hemozy location - MVP Colony, Visakhapatnam"
                src="https://www.google.com/maps?q=MVP+Colony,+Visakhapatnam,+Andhra+Pradesh&output=embed"
                className="w-full h-full border-0" loading="lazy" data-testid="contact-map" />
            </div>
            <form onSubmit={submit} className="bg-white rounded-[1.75rem] border border-slate-200/90 p-7" data-testid="contact-form">
              <h3 className="font-bold text-[#0B192C] text-lg mb-5">Send us a message</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" data-testid="contact-name-input"
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="Phone *" inputMode="numeric" data-testid="contact-phone-input"
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              </div>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email (optional)" data-testid="contact-email-input"
                className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="How can we help? *" data-testid="contact-message-input"
                className="mt-4 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button type="submit" disabled={sending} data-testid="contact-submit-button"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#0B192C] hover:bg-[#1E3E62] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? "Sending..." : "Send message"}
                </button>
                <button type="button" onClick={() => onBook([])} data-testid="contact-book-button"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98]">
                  Book appointment instead
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
